/**
 * Legal Document Versions and Constants
 * 
 * Single source of truth for legal document versions and related text.
 * Update these constants when legal documents are updated.
 */

// V179.2: Removed hardcoded versions - versions now come from database
// Use useLegalVersions() hook to get current versions dynamically

export const LEGAL_URLS = {
  PRIVACY: '/legal/privacy',
  TERMS: '/legal/terms',
  COMMUNITY: '/legal/community',
  REFUND: '/legal/refund',
  WEDDING_GIFT: '/legal/wedding-gift',
  SAFETY: '/legal/safety',
} as const;

export const LEGAL_CONTACT = {
  EMAIL: 'blissnetapp@gmail.com',
  MAILING_ADDRESS: 'BlissNet Technologies Ltd.\nc/o Systems-il\nBerz 6, Petah Tikva, Israel',
} as const;

/**
 * V213.1: Billing text for platform app stores
 * V338: Updated for Apple App Store compliance — multi-platform wording
 */
export const BILLING_TEXT = {
  MERCHANT_OF_RECORD: 'Subscriptions are billed and managed through the platform on which you use the app (including Apple App Store and Google Play). The respective platform handles payment processing and receipts.',
  MERCHANT_OF_RECORD_SHORT: 'Managed via App Store or Google Play',
  FOOTER_TEXT: 'BlissNet subscriptions are managed via the platform on which you use the app (Apple App Store or Google Play).',
} as const;

export const ENCRYPTION_TEXT = {
  FULL: 'Messages are encrypted in transit (TLS 1.3) and securely stored using industry-standard AES-256 encryption. They are not end-to-end encrypted, meaning BlissNet can access message content for safety and moderation purposes.',
  SHORT: 'Encrypted in transit and securely stored. Not end-to-end encrypted.',
} as const;

export const TRUST_SCORE_FORMULA = {
  DESCRIPTION: 'Trust Score = (Direct Friends × 10) + (Friends of Friends × 1) + (Verified ? 100 : 0)',
  DIRECT_FRIEND_POINTS: 10,
  SECOND_DEGREE_POINTS: 1,
  VERIFICATION_BONUS: 100,
} as const;

/**
 * Centralized verification text to prevent drift across UI components
 * MUST match legal documents in /legal directory
 */
export const VERIFICATION_TEXT = {
  /**
   * Short description for tooltips and quick info
   */
  SHORT: 'Verification is an optional $10/month subscription that adds +100 to your Trust Score while active, gives a verified badge and visibility boost, and supports network-wide safety checks. It is not ID or background check and does not guarantee safety or matches.',

  /**
   * What verification IS
   */
  WHAT_IT_IS: 'Verification is an optional $10/month subscription that adds +100 points to your Trust Score while it is active and unlocks additional benefits.',

  /**
   * Benefits list (for UI display)
   */
  BENEFITS: [
    {
      icon: '✔',
      title: 'Instant +100 Trust Score',
      description: 'Shows real commitment to safety and significantly boosts your trust ranking.',
    },
    {
      icon: '✔',
      title: 'Verified Badge',
      description: 'A clear signal to others that your profile is authentic and secure.',
    },
    {
      icon: '✔',
      title: 'Higher Visibility in Explore',
      description: 'Verified users appear more prominently because they contribute to a safer network.',
    },
    {
      icon: '✔',
      title: 'Priority Support',
      description: 'Faster help from the BlissNet support team.',
    },
    {
      icon: '✔',
      title: 'Stronger Network Integrity',
      description: 'Our safety and analytics team performs periodic checks across the BlissNet network to detect and remove potential fake or harmful accounts, improving the safety and authenticity of everyone\'s experience.',
    },
  ] as const,

  /**
   * Network safety checks explanation
   */
  SAFETY_CHECKS: 'These checks do NOT require collecting additional personal data beyond what\'s needed for basic app operation and fraud prevention. They rely on existing signals such as Trust Score patterns, social graph irregularities, abuse reports, suspicious connection behavior, and verification status.',

  /**
   * What verification is NOT (critical disclaimers)
   */
  DISCLAIMER: 'Verification is a subscription feature, not government ID verification or a background check. It does not guarantee safety, authenticity, or matches.',

  /**
   * Detailed "what it is NOT" list
   */
  NOT_LIST: [
    'Verification is NOT government ID verification',
    'Verification is NOT a background check',
    'Verification does NOT guarantee safety, authenticity, or matches',
    'Verification does NOT provide immunity from reports or bans',
  ] as const,

  /**
   * How it works (for payment/subscription info)
   */
  HOW_IT_WORKS: [
    'Subscribe through the platform on which you use the app (Apple App Store or Google Play)',
    'Auto-renews monthly unless canceled',
    'Cancel anytime from app settings or app store settings',
    'Trust Score bonus active only while subscription is active',
  ] as const,

  /**
   * Price
   */
  PRICE: '$10/month',

  /**
   * Trust Score bonus
   */
  TRUST_SCORE_BONUS: 100,
} as const;

/**
 * Trust Score text constants
 */
export const TRUST_SCORE_TEXT = {
  /**
   * Complete Trust Score formula explanation
   */
  FORMULA: '(direct_friends × 10) + (second_degree_friends × 1) + (verified ? 100 : 0)',

  /**
   * Short explanation for tooltips and quick summaries (Quick Summary bullet)
   */
  SHORT: 'Score = 10 per direct friend, 1 per second-degree friend, +100 while your verification subscription is active',

  /**
   * Detailed explanation for info cards
   */
  DETAILED: `Your Trust Score is calculated using:
• Direct friends (1st degree): 10 points each
• Friends of friends (2nd degree): 1 point each
• Verification status: +100 points while your verification subscription is active

Trust Score is designed to reflect the strength of your real social network and your commitment to safety.`,

  /**
   * Disclaimer for all Trust Score displays
   */
  DISCLAIMER: 'Trust Score is NOT a guarantee of character, safety, or authenticity — you must always use your own judgment and follow safety tips.',

  /**
   * Bullet list for Final Note card (prevents drift)
   */
  BULLETS: [
    '10 points per direct friend',
    '1 point per friend-of-friend (2nd degree)',
    '+100 points while your verification subscription is active ($10/month via App Store or Google Play)',
  ] as const,

  /**
   * One-line description for Final Note intro
   */
  DESCRIPTION_SHORT: 'The Trust Score is transparent, simple, and fair:',
} as const;

// V179.2: Removed TERMS_QUICK_SUMMARY_FALLBACK and PRIVACY_QUICK_SUMMARY_FALLBACK
// All legal content must come from database - NO fallbacks to hardcoded content

